import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, HelpCircle, Rocket, FileText, Gauge, Coins, Wallet, Wrench, Handshake, Search, X } from "lucide-react";
import { FUNDER_SECTIONS } from "../lib/funderFaq";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export const SECTIONS = [
  {
    id: "about",
    icon: HelpCircle,
    title: "About Clinq",
    faqs: [
      { q: "What is Clinq Africa?",
        a: "Clinq Africa is a platform that helps businesses prepare for and access funding. We help founders build the documentation funders require, assess their capital readiness through a scored report, and connect them with suitable funding sources from a database of 500+ active funders across Kenya and East Africa." },
      { q: "What size of business is Clinq designed for?",
        a: "Clinq is built for businesses looking to raise capital below $1 million — roughly 1 million to 100 million Kenyan shillings. We work with early-stage and growing businesses at various stages of their funding journey. If you are a business looking to raise USD 1M+ do reach out to capital@clinq.africa where we will connect you to professional advisors who can offer a more personalised service." },
      { q: "What industries does Clinq work with?",
        a: "Clinq works across multiple sectors including AgriTech, FinTech, HealthTech, EdTech, Logistics, E-Commerce, SaaS, Manufacturing, CleanTech, and Professional Services. Our capital readiness methodology and financial model templates are tailored by industry." },
      { q: "Where is Clinq available?",
        a: "Clinq is currently available to businesses based in Kenya and is open to businesses across East Africa." },
      { q: "What is the funding gap Clinq is trying to solve?",
        a: "Most funders have minimum ticket sizes that exclude small and growing businesses, while most small businesses lack the documentation and preparation needed to approach the funders that do exist. Clinq bridges that gap by preparing businesses to meet funder requirements and matching them to the right opportunities." },
    ],
  },
  {
    id: "getting-started",
    icon: Rocket,
    title: "Getting Started",
    faqs: [
      { q: "How do I get started on Clinq?",
        a: "Register on the Clinq platform, complete the business snapshot to create your profile, and upload any existing business documentation you have. From there the platform will guide you through building your investor materials and generating your Capital Readiness Score." },
      { q: "What information do I need to get started?",
        a: "Start with what you have. At minimum you will need basic business information — your industry, revenue stage, and business structure. The more documentation you upload (pitch deck, financials, bank statements), the more complete and accurate your Capital Readiness Report will be." },
      { q: "How long does the capital readiness process take?",
        a: "Building your documentation and generating your Capital Readiness Report typically takes a few days to a few weeks depending on how much existing documentation you have. The funding process itself varies — from four weeks for debt financing to up to twelve months for equity rounds." },
    ],
  },
  {
    id: "documentation",
    icon: FileText,
    title: "Documentation & Preparation",
    faqs: [
      { q: "What documentation do funders typically require?",
        a: "Most funders will require some combination of the following: a pitch deck, a financial model covering historical performance and future projections, management accounts for the last 12 to 24 months, bank statements, and a certificate of incorporation. Audited financials are required for formal due diligence and should be prepared as early as possible." },
      { q: "Does Clinq help me build these documents?",
        a: "Yes. The platform includes a pitch deck builder, a financial model builder, and tools to help you organise and present your management accounts. Our Capital Readiness Report also identifies which documents are incomplete or missing and guides you on what to add." },
      { q: "Why do I need a financial model if I already have a pitch deck?",
        a: "A pitch deck tells your story. A financial model proves the numbers behind it. Funders use the financial model to test your assumptions, understand how you will deploy capital, and assess when the business turns profitable. A pitch deck without a financial model will not get past a first meeting." },
      { q: "Why are management accounts and bank statements important?",
        a: "Funders use management accounts and bank statements to verify that the numbers in your financial model reflect what has actually happened in your business. They are essential for due diligence — without them, even a strong pitch deck and financial model will not be enough to close a deal." },
      { q: "Are audited financials required?",
        a: "Audited financials are not always required to start the process but are a standard requirement before any funder completes due diligence. We recommend getting them in order as early as possible — they are one of the most common reasons deals are delayed." },
      { q: "How do I demonstrate skin in the game to investors?",
        a: "Investors want evidence that you have personally invested in the business. Your financial model should include a clear breakdown of historical expenditure — covering infrastructure, staff costs, office space, and any founder capital injected. Verified costs supported by bank statements and receipts carry the most weight." },
    ],
  },
  {
    id: "score",
    icon: Gauge,
    title: "Capital Readiness Score",
    faqs: [
      { q: "What is the Capital Readiness Score?",
        a: "The Capital Readiness Score is a score out of 100 that assesses how prepared your business is to approach funders. It evaluates your documentation, financial model quality, business fundamentals, and growth defensibility across a set of weighted criteria. The score falls into one of three bands: Funder-Ready (80 to 100), Nearly There (60 to 79), or Not Ready (below 60)." },
      { q: "How does the Capital Readiness Report work?",
        a: "Once you upload your documentation and complete your financial model on the platform, Clinq generates a Capital Readiness Report. The report gives you your score, identifies specific gaps in your documentation and financial model, and provides a shortlist of funders best suited to your business based on your sector, stage, and capital needs." },
      { q: "What happens after I receive my Capital Readiness Score?",
        a: "The report gives you a prioritised list of actions to take before approaching funders. For businesses scoring below 80, the report identifies exactly what is holding the score back and what to fix. Once gaps are addressed, you can regenerate the report and improve your score before initiating funder outreach." },
      { q: "What role does AI play in my financial analysis?",
        a: "The AI processes the data you provide and uses it to build your financial model, flag overly aggressive projections, and generate your Capital Readiness Report. The output is only as strong as the data you input — the platform does not generate or fabricate figures on your behalf." },
    ],
  },
  {
    id: "funding-types",
    icon: Coins,
    title: "Types of Funding",
    faqs: [
      { q: "What is the difference between equity and debt funding?",
        a: "Equity funding means a funder takes a stake in your business in exchange for capital. You do not repay the investment but you give up a percentage of ownership. Debt funding means you borrow capital and repay it over time with interest. You retain full ownership but take on a repayment obligation. The right choice depends on your business model, revenue stage, and growth trajectory." },
      { q: "What types of funders does Clinq connect businesses with?",
        a: "Clinq connects businesses with a range of funder types including angel investors, venture capital funds, private equity, development finance institutions (DFIs), impact investors, and debt providers including banks and alternative lenders." },
      { q: "What interest rates should I expect for debt financing?",
        a: "Rates vary by provider. Commercial banks typically offer 14 to 18 percent per year. Alternative debt providers typically range from 18 to 30 percent per year. USD-denominated debt facilities may be available at 13 to 18 percent depending on the provider and structure." },
      { q: "Should I pursue equity or debt?",
        a: "It depends on what the capital is for. If you need working capital to fund operations or stock, debt is generally more appropriate — it lets you retain ownership while covering short-term needs. If you are raising capital to fund growth, build a team, or expand into new markets, equity is typically better suited. Clinq's Capital Readiness Report will recommend the most appropriate capital type for your specific situation." },
      { q: "What is revenue-based financing?",
        a: "Revenue-based financing is a form of capital where you repay the funder as a percentage of your monthly revenue rather than through fixed repayments. It is well suited to businesses with consistent and predictable monthly revenue. Repayment amounts flex with your revenue — you pay more in strong months and less in slower months." },
      { q: "How long does it take to secure funding?",
        a: "Timelines vary significantly by funder type. Debt financing through alternative lenders can move in two to four weeks once documentation is in order. Commercial bank loans typically take four to twelve weeks. Equity rounds with VCs or DFIs can take three to twelve months from first meeting to drawdown." },
    ],
  },
  {
    id: "pricing",
    icon: Wallet,
    title: "Fees & Pricing",
    faqs: [
      { q: "What does it cost to use Clinq?",
        a: "Clinq operates on two fee types. Platform fees are credit-based — you purchase credits to use the platform tools, including the pitch deck builder, financial model builder, and Capital Readiness Report, with credit packages starting from KES 2,999. Success fees apply when capital is successfully raised through Clinq's funder network — up to 5 percent of the capital raised, payable only upon drawdown." },
      { q: "When is the success fee charged?",
        a: "The success fee of up to 5 percent is only charged after capital has been successfully raised and the funds have arrived in your account. There is no success fee if a funding round does not close." },
      { q: "Are there any other fees?",
        a: "Beyond platform credits and the success fee, there are no hidden fees. The credit cost for each platform action is displayed before you use it." },
    ],
  },
  {
    id: "tools",
    icon: Wrench,
    title: "Platform Tools",
    faqs: [
      { q: "What tools does the Clinq platform offer?",
        a: "The platform currently includes a pitch deck builder with industry-specific templates, a financial model builder with a guided question-and-answer flow that generates a downloadable Excel model, a Capital Readiness Report and scoring engine, and a funder matching tool that connects your business profile to suitable funding sources." },
      { q: "Can Clinq help with business valuation?",
        a: "Yes. The platform provides valuation guidance to help you understand what your business is worth before entering equity conversations with funders. Understanding your valuation is critical for negotiating equity stakes and protecting your ownership position." },
      { q: "How can I demonstrate scalability to investors?",
        a: "Scalability is best shown by combining your historical track record with a credible forward projection. Use your actual revenue and transaction history as the base, then show how specific capital injections will enable you to scale — whether through expanding your customer base, entering new markets, or improving operational capacity. Your financial model should include scenario analysis showing different growth outcomes." },
    ],
  },
  {
    id: "funders",
    icon: Handshake,
    title: "Working with Funders",
    faqs: [
      { q: "How does Clinq connect me with funders?",
        a: "Once your Capital Readiness Score meets the threshold for your target funder type, Clinq matches your business profile against its database of 500+ active funders. Matches are based on your sector, stage, capital type, geography, and raise size. Clinq supports you in preparing for and initiating funder outreach." },
      { q: "How do I approach funders effectively?",
        a: "Lead with traction, not just your idea. Have your documentation complete before any outreach. Warm introductions significantly outperform cold approaches — use your network to get referred where possible. Know your numbers cold before any meeting — funders will ask about revenue, margins, burn rate, and use of funds in the first conversation. A clear, specific ask with a defined use of funds is far more effective than a vague funding request." },
      { q: "What is the typical funder engagement process?",
        a: "The process generally follows this sequence: initial meeting and expression of interest, term sheet, due diligence (financial, legal, and operational review), final negotiations, legal documentation, and drawdown. The length of each stage varies by funder type — equity rounds involve more due diligence and take longer than debt facilities." },
      { q: "What are the most common mistakes founders make when approaching funders?",
        a: "Approaching funders before documentation is complete. Overstating projections — funders stress-test models and will catch it. Going silent when performance dips — transparency with funders builds more trust than silence. Giving away too much equity too early. Not understanding the terms in a term sheet before signing." },
      { q: "What should I do if I have questions or encounter issues on the platform?",
        a: "Contact the Clinq team directly at support@clinq.africa or capital@clinq.africa. Our team will respond as soon as possible." },
    ],
  },
];

export default function FAQ() {
  const [params, setParams] = useSearchParams();
  const audience = params.get("for") === "funders" ? "funders" : "business";
  const isFunder = audience === "funders";
  const ACTIVE_SECTIONS = isFunder ? FUNDER_SECTIONS : SECTIONS;
  const setAudience = (a) => {
    setParams(a === "funders" ? { for: "funders" } : {}, { replace: true });
    setQuery("");
  };
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const visibleSections = useMemo(() => {
    if (!q) return ACTIVE_SECTIONS;
    return ACTIVE_SECTIONS.map((s) => ({
      ...s,
      faqs: s.faqs.filter(
        (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)),
    })).filter((s) => s.faqs.length > 0);
  }, [q, ACTIVE_SECTIONS]);
  const matchCount = q ? visibleSections.reduce((n, s) => n + s.faqs.length, 0) : null;

  return (
    <div data-testid="faq-page" className="min-h-screen bg-white text-neutral-900"
      style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />

      <header className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-4xl px-6 py-14 lg:py-20">
          <Link to="/" data-testid="faq-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-[#7BD3CE]">
            {isFunder ? "For Funders · Vetted Deal Flow" : "For Business Owners · Unlock Your Capital Potential"}
          </p>
          <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">
            Frequently Asked Questions
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            {isFunder
              ? "Everything funders ask us about Clinq — vetting, the Capital Readiness Score, document stacks by capital type, introductions, and deal timelines."
              : "Everything business owners ask us about Clinq — from getting started and building your documentation to fees, funding types, and working with funders."}
          </p>
          <div className="mt-6 inline-flex rounded-full border border-white/20 bg-white/5 p-1"
            data-testid="faq-audience-toggle">
            <button type="button" data-testid="faq-audience-business"
              onClick={() => setAudience("business")}
              className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${
                !isFunder ? "bg-[#F59E0B] text-[#04302E]" : "text-white/60 hover:text-white"}`}>
              For Business Owners
            </button>
            <button type="button" data-testid="faq-audience-funders"
              onClick={() => setAudience("funders")}
              className={`rounded-full px-5 py-2 text-xs font-bold transition-colors ${
                isFunder ? "bg-[#F59E0B] text-[#04302E]" : "text-white/60 hover:text-white"}`}>
              For Funders
            </button>
          </div>
          <div className="relative mt-8 max-w-xl" data-testid="faq-search">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input type="text" value={query}
              data-testid="faq-search-input"
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions — e.g. fees, pitch deck, equity…"
              className="w-full rounded-full border border-white/20 bg-white/10 py-3 pl-11 pr-11 text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-[#7BD3CE]" />
            {query && (
              <button type="button" onClick={() => setQuery("")}
                data-testid="faq-search-clear"
                aria-label="Clear search"
                className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-white/50 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {matchCount !== null && (
            <p className="mt-3 text-xs font-semibold text-white/50" data-testid="faq-search-count">
              {matchCount === 0 ? "No questions match your search"
                : `${matchCount} question${matchCount === 1 ? "" : "s"} match your search`}
            </p>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12 lg:py-16">
        {/* Section jump links (hidden while searching) */}
        {!q && (
          <nav className="mb-10 flex flex-wrap gap-2" data-testid="faq-section-nav">
            {ACTIVE_SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`}
                data-testid={`faq-nav-${s.id}`}
                className="rounded-full border border-neutral-200 px-4 py-1.5 text-xs font-bold text-neutral-600 transition-colors hover:border-[#0A8B85] hover:text-[#0A8B85]">
                {s.title}
              </a>
            ))}
          </nav>
        )}

        {q && visibleSections.length === 0 && (
          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 text-center"
            data-testid="faq-no-results">
            <p className="text-sm font-bold text-neutral-800">
              No answers found for “{query.trim()}”.
            </p>
            <p className="mt-2 text-sm text-neutral-600">
              Try a different word — or email{" "}
              <a href="mailto:support@clinq.africa" className="font-semibold text-[#0A8B85] underline-offset-4 hover:underline">
                support@clinq.africa
              </a>{" "}
              and we'll answer directly.
            </p>
          </div>
        )}

        {visibleSections.map((s) => (
          <section key={s.id} id={s.id} data-testid={`faq-section-${s.id}`}
            className="scroll-mt-24 border-t border-neutral-200 py-10 first-of-type:border-t-0 first-of-type:pt-0">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0A8B85]/10 text-[#0A8B85]">
                <s.icon className="h-5 w-5" />
              </div>
              <h2 className="text-xl font-bold tracking-tight text-neutral-900 sm:text-2xl">
                {s.title}
              </h2>
            </div>
            <Accordion
              type="multiple"
              className="mt-4"
              {...(q ? { value: s.faqs.map((_, i) => `${s.id}-${i}`) } : {})}>
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`${s.id}-${i}`}
                  data-testid={`faq-item-${s.id}-${i}`}>
                  <AccordionTrigger className="text-left text-[15px] font-bold text-neutral-800 hover:text-[#0A8B85] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-neutral-700">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}

        <div className="mt-4 rounded-2xl bg-[#04302E] p-8 text-white" data-testid="faq-cta">
          <h2 className="text-xl font-extrabold sm:text-2xl">Still have a question?</h2>
          <p className="mt-2 max-w-xl text-sm text-white/70">
            {isFunder ? (
              <>Talk to us about deal flow access and partnership terms at{" "}
                <a href="mailto:capital@clinq.africa" className="font-bold text-[#7BD3CE] underline-offset-4 hover:underline">capital@clinq.africa</a>.</>
            ) : (
              <>Our team is happy to help — reach us at{" "}
                <a href="mailto:support@clinq.africa" className="font-bold text-[#7BD3CE] underline-offset-4 hover:underline">support@clinq.africa</a>{" "}
                or{" "}
                <a href="mailto:capital@clinq.africa" className="font-bold text-[#7BD3CE] underline-offset-4 hover:underline">capital@clinq.africa</a>.</>
            )}
          </p>
          <Link to={isFunder ? "/get-started-funder" : "/join"} data-testid="faq-cta-join"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-6 py-3 text-sm font-bold text-[#04302E] hover:opacity-90">
            {isFunder ? "Register as a funder" : "Get started on Clinq"}
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
